import { useCallback } from 'react'
import { Autocomplete, Box, Paper, TextField, Typography } from '@mui/material'
import { useMediaQuery, useTheme } from '@mui/material'
import { Add } from '@mui/icons-material'
import { matchSorter } from 'match-sorter'
import { TpTooltip } from '~/components'
import { useCropModalContext } from '../context/crop-modal-context'
import { useCropVarieties } from '../hooks/use-crop-varieties'
import { DESKTOP_BREAKPOINT } from '~/common/constants/layout-constants'
import { SELECT_CROP_TOOLTIP_TEXT } from '../constants/crop-modal-constants'

const filterVarietyOptions = (options, { inputValue }) => matchSorter(options, inputValue, { keys: ['name'] })

export const CropVarietyField = ({ onVarietyChange, handlers }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_BREAKPOINT))

  const { species, variety, isCustomCrop, isCustomVariety, customVarietyName } = useCropModalContext()
  const { varieties } = useCropVarieties({ speciesId: species?.id })

  const CustomVarietyPaper = useCallback(({ children, ...props }) => (
    <Paper {...props}>
      {children}
      <Box
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => handlers?.handleCustomVarietyToggle?.(true)}
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          p: 1,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Add fontSize="small" color="action" />
        <Typography variant="body2" color="text.secondary">Custom Variety...</Typography>
      </Box>
    </Paper>
  ), [handlers])

  if (isCustomVariety) {
    return (
      <TextField
        size={isDesktop ? 'medium' : 'small'}
        autoFocus
        fullWidth
        label="Custom Variety Name (optional)"
        value={customVarietyName}
        onChange={(e) => handlers?.handleCustomVarietyNameChange?.(e.target.value)}
        inputProps={{ maxLength: 100 }}
        helperText={
          <Box
            component="span"
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => handlers?.handleCustomVarietyToggle?.(false)}
          >
            Use template variety
          </Box>
        }
      />
    )
  }

  return (
    <TpTooltip followCursor placement='top' title={(!species && !isCustomCrop) ? SELECT_CROP_TOOLTIP_TEXT : ''}>
      <Autocomplete
        size={isDesktop ? 'medium' : 'small'}
        filterOptions={filterVarietyOptions}
        disablePortal
        options={varieties}
        value={variety}
        disabled={!species && !isCustomCrop}
        getOptionLabel={(opt) => opt?.name ?? ''}
        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
        slots={{ paper: CustomVarietyPaper }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Crop Variety (optional)"
            placeholder="Select variety"
          />
        )}
        onChange={onVarietyChange}
      />
    </TpTooltip>
  )
}
